import { isQuinnChartType, QuinnCopy, QuinnMessage, QuinnRole } from "../types";
import type { QuinnChartSpec } from "../components/blocks/QuinnChartBlock.tsx";
import { isQuinnTableSpec, type QuinnTableSpec } from "../components/blocks/QuinnTableBlock.tsx";

export type QuinnArtifact =
    | { id: string; kind: "chart"; title: string; subtitle: string | null; summary: string; clientBusinessId: string | null; drawnForAllClients: boolean; spec: QuinnChartSpec; messageId: string }
    | { id: string; kind: "table"; title: string; subtitle: string | null; summary: string; clientBusinessId: string | null; drawnForAllClients: boolean; spec: QuinnTableSpec; messageId: string };

function count(amount: number, singular: string, plural: string): string {
    return `${amount} ${amount === 1 ? singular : plural}`;
}

const ARTIFACT_FENCE_RE = /```quinn:(chart|table)\r?\n([\s\S]*?)\r?\n?```/g;

export function parseQuinnArtifact(kind: string, raw: string, messageId: string): QuinnArtifact | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }
    if (!parsed || typeof parsed !== "object") return null;
    const id = (parsed as { id?: unknown }).id;
    if (typeof id !== "string" || id.length === 0) return null;
    const client = (parsed as { client_business_id?: unknown }).client_business_id;
    const clientBusinessId = typeof client === "string" && client ? client : null;
    const drawnForAllClients = (parsed as { client_scope?: unknown }).client_scope === "all_clients";

    if (kind === "chart") {
        const spec = parsed as QuinnChartSpec;
        if (!isQuinnChartType(spec.chart_type) || !Array.isArray(spec.points) || spec.points.length === 0) return null;
        return {
            id,
            kind: "chart",
            title: spec.title?.trim() || QuinnCopy.Chart,
            subtitle: spec.series_name?.trim() || null,
            summary: `${QuinnCopy.Chart} · ${count(spec.points.length, QuinnCopy.ChartPoint, QuinnCopy.ChartPoints)}`,
            spec,
            clientBusinessId,
            drawnForAllClients,
            messageId,
        };
    }
    if (kind === "table" && isQuinnTableSpec(parsed)) {
        return {
            id,
            kind: "table",
            title: parsed.title,
            subtitle: parsed.subtitle ?? null,
            summary: `${QuinnCopy.Table} · ${count(parsed.rows.length, QuinnCopy.TableRow, QuinnCopy.TableRows)}`,
            spec: parsed,
            clientBusinessId,
            drawnForAllClients,
            messageId,
        };
    }
    return null;
}

export function extractArtifacts(messages: QuinnMessage[]): QuinnArtifact[] {
    const artifacts: QuinnArtifact[] = [];
    for (const message of messages) {
        if (message.role !== QuinnRole.Quinn) continue;
        for (const match of message.content.matchAll(ARTIFACT_FENCE_RE)) {
            const artifact = parseQuinnArtifact(match[1], match[2], message.id);
            if (artifact) artifacts.push(artifact);
        }
    }
    return artifacts;
}
